//@flow
import React from 'react';
import { fetchQuery } from 'react-relay';

import type { RelayProp } from 'react-relay';

type Props = {
    query: Object,
    renderVariables: Object,
    variables: Object,
    environment: ?Object,
    render: Function,
};

type State = {
    data: Object,
};

type Context = {
    relay: RelayProp,
};

class SharedParallelizedQueryRenderer extends React.Component<Props, State> {
    constructor(props: Props, context: Context) {
        super(props, context);

        this.state = { data: {} };

        this.getData = this.getData.bind(this);

        this.fetchQueryInProgress = false;
        this.queuedFetchQueryVariables = null;

        this._relayContext = {
            environment: this.props.environment,
            variables: {
                ...(props.renderVariables || {}),
                ...(props.variables || {}),
            },
        };
    }

    getChildContext() {
        return {
            relay: this._relayContext,
        };
    }

    componentDidMount() {
        this.getData();
    }

    /**
     * NOTE: `componentDidUpdate` checks that the "instance" of the variables object in props passed has changed to
     * determine whether to fetch more data. Even if the values of the keys of the variables object are the same, if
     * the variables object is a different reference this will trigger a fetch.
     */
    componentDidUpdate(prevProps: Props) {
        // Is there a fetchQuery that has not yet completed?
        if (this.fetchQueryInProgress) {
            // If previous fetchQuery has not resolved but we detect change in variables, stash variables
            if (prevProps.variables !== this.props.variables) {
                this.queuedFetchQueryVariables = this.props.variables;
            }
        }
        // If there is a change in variables and there is no fetchQuery in progress, get moar data!
        else if (prevProps.variables !== this.props.variables) {
            this.getData();
        }
        // Are there queued variables from a previous update?
        // If queued variables are the same as those in props, get data
        else if (
            this.queuedFetchQueryVariables &&
            this.queuedFetchQueryVariables === this.props.variables
        ) {
            this.queuedFetchQueryVariables = null;
            this.getData();
        }
        // If queued variables are not same as those in props, assume queued variables are stale, get data not needed
        else if (
            this.queuedFetchQueryVariables &&
            this.queuedFetchQueryVariables !== this.props.variables
        ) {
            this.queuedFetchQueryVariables = null;
        }
    }

    getData: () => Promise<mixed>;
    fetchQueryInProgress: boolean;
    queuedFetchQueryVariables: ?Object;
    _relayContext: {
        environment: ?Object,
        variables: Object,
    };

    getData() {
        const { environment, query, variables } = this.props;
        this.fetchQueryInProgress = true;

        if (!environment) {
            return;
        }

        fetchQuery(environment, query, variables)
            .then(data => {
                this.fetchQueryInProgress = false;
                this._relayContext.variables = {
                    ...(this.props.renderVariables || {}),
                    ...(this.props.variables || {}),
                };
                this.setState({ data });
            })
            .catch(() => {
                this.fetchQueryInProgress = false;
            });
    }

    render() {
        const { data } = this.state;
        return this.props.render({ data });
    }
}

export { SharedParallelizedQueryRenderer };
